import queryString, { ParsedQuery } from 'query-string'
import { ComplianceFramework } from '@trustero/trustero-api-web/lib/audit/framework_pb'
import {
  AuditFilter,
  ComplianceFrameworkFilter,
  ControlFilter,
  FrameworkTypeFilter,
  GetComplianceFrameworksRequest,
} from '@trustero/trustero-api-web/lib/audit/audit_pb'
import { BoolValue } from 'google-protobuf/google/protobuf/wrappers_pb'
import { NONE_ID } from 'src/Utils/globalConstants'
import { FilterParam } from 'src/components/Reusable/IndexPage/FilterBar/FilterBar.types'
import { queryParamAsArray } from 'src/Utils/queryParams'
import {
  GridColumnSortFrameworks,
  GridColumnSortType,
} from 'src/components/Reusable/Grid/GridColumnSort/GridColumnSort.constants'

// URL values for the framework_type[] filter; matches FrameworkTypeFilterDropdown options.
export const FRAMEWORK_TYPE_FILTER_VALUE = {
  CUSTOM: 'custom',
  CATALOG: 'catalog',
} as const

const getFrameworkSortValue = (
  framework: ComplianceFramework,
  col: GridColumnSortFrameworks,
  counts: {
    sectionsByFrameworkId: Record<string, number>
    requirementsByFrameworkId: Record<string, number>
    controlsByFrameworkId: Record<string, number>
    auditsByFrameworkId: Record<string, number>
  },
): string | number => {
  const id = framework.getId()
  switch (col) {
    case GridColumnSortFrameworks.NAME:
      return framework.getName().toLowerCase()
    case GridColumnSortFrameworks.DESCRIPTION:
      return framework.getDescription().toLowerCase()
    case GridColumnSortFrameworks.SECTIONS:
      return counts.sectionsByFrameworkId[id] ?? 0
    case GridColumnSortFrameworks.REQUIREMENTS:
      return counts.requirementsByFrameworkId[id] ?? 0
    case GridColumnSortFrameworks.CONTROLS:
      return counts.controlsByFrameworkId[id] ?? 0
    case GridColumnSortFrameworks.AUDITS:
      return counts.auditsByFrameworkId[id] ?? 0
    default:
      return ''
  }
}

// Apply a single filter dimension to the request based on URL params.
// Mirrors the per-page applyFilter pattern used by Controls / Policies / Evidence.
const applyFrameworksFilter = (
  request: GetComplianceFrameworksRequest,
  queryParams: ParsedQuery<string>,
  filterType: FilterParam,
): void => {
  const filterParams = queryParamAsArray(queryParams[filterType])
  if (!filterParams?.length) return

  switch (filterType) {
    case FilterParam.AUDIT: {
      const auditIds = new Set(filterParams)
      const includeNone = auditIds.delete(NONE_ID)
      const filter = new AuditFilter().setAuditIdsList(Array.from(auditIds))
      if (includeNone) {
        filter.setIncludeNone(new BoolValue().setValue(true))
      }
      request.setAuditFilter(filter)
      break
    }
    case FilterParam.CONTROL: {
      const controlIds = new Set(filterParams)
      const includeNone = controlIds.delete(NONE_ID)
      const filter = new ControlFilter().setControlIdsList(
        Array.from(controlIds),
      )
      if (includeNone) {
        filter.setIncludeNone(new BoolValue().setValue(true))
      }
      request.setControlFilter(filter)
      break
    }
    case FilterParam.FRAMEWORK_TYPE: {
      const types = new Set(filterParams)
      const filter = new FrameworkTypeFilter()
        .setIncludeCustom(types.has(FRAMEWORK_TYPE_FILTER_VALUE.CUSTOM))
        .setIncludeCatalog(types.has(FRAMEWORK_TYPE_FILTER_VALUE.CATALOG))
      request.setFrameworkTypeFilter(filter)
      break
    }
    case FilterParam.NAME: {
      const filter =
        new ComplianceFrameworkFilter().setComplianceFrameworkIdsList(
          filterParams,
        )
      request.setComplianceFrameworkFilter(filter)
      break
    }
    default:
      break
  }
}

// Build the GetComplianceFrameworksRequest from the URL search string.
// excludeDisabled is always true for the index page (matches existing behavior).
export const getFrameworksFilterRequest = (
  locSearch: string,
): GetComplianceFrameworksRequest => {
  const queryParams = queryString.parse(locSearch, { arrayFormat: 'bracket' })
  const request = new GetComplianceFrameworksRequest().setExcludeDisabled(true)
  Object.values(FilterParam).forEach((filterType) =>
    applyFrameworksFilter(request, queryParams, filterType),
  )
  return request
}

export const sortFrameworks = (
  frameworks: ComplianceFramework[],
  sortCol: GridColumnSortFrameworks,
  sortType: GridColumnSortType,
  counts: {
    sectionsByFrameworkId: Record<string, number>
    requirementsByFrameworkId: Record<string, number>
    controlsByFrameworkId: Record<string, number>
    auditsByFrameworkId: Record<string, number>
  },
): ComplianceFramework[] => {
  const sorted = [...frameworks].sort((a, b) => {
    const av = getFrameworkSortValue(a, sortCol, counts)
    const bv = getFrameworkSortValue(b, sortCol, counts)
    if (av < bv) return -1
    if (av > bv) return 1
    return 0
  })
  if (sortType === GridColumnSortType.DESCENDING) sorted.reverse()
  return sorted
}
